import { FirstNFreeDiscountDtoMapper } from '@domains/billing-settings/priv/CustomerDiscount/discounts/first-n-free'
import { PerUnitDiscountDtoMapper } from '@domains/billing-settings/priv/CustomerDiscount/discounts/per-unit/PerUnitDiscountDtoMapper'
import { PerUnitAndFlatDiscountDtoMapper } from '@domains/billing-settings/priv/CustomerDiscount/discounts/per-unit-and-flat/PerUnitAndFlatDiscountDtoMapper'
import type {
  AssignedPrice,
  AssignedPriceAlgorithm,
  LinearAlgorithm,
  BillableItem as SwaggerBillableItem,
} from '@mynt/backoffice-api-spec/billables'
import type { CreateDiscountRequest, Discount } from '@mynt/backoffice-api-spec/discounts'
import type { Template as ContractTemplate } from '@mynt/backoffice-api-spec/templates'
import { FlatDiscountDtoMapper } from '../discounts/flat'
import { LinearPriceParser } from '../prices/linear'
import type { Amount, AmountWithType, Billable, BillablePrice, CreateDiscount, CustomerDiscount, Discounts, Template } from './api'

export interface BaseBillablePrice {
  billableId: string
  algorithm: AssignedPriceAlgorithm
}

export abstract class BillablePriceParser {
  abstract supports(type: string): boolean
  abstract parse(price: BaseBillablePrice): BillablePrice
  abstract parseAmountWithTypeFromConstantsAndCoefficients(
    constants: LinearAlgorithm['constants'],
    coefficients: LinearAlgorithm['coefficients'],
  ): AmountWithType[]
}

export class PriceParser {
  private parsers: BillablePriceParser[] = [new LinearPriceParser()]

  private getParserForPrice(type: string): BillablePriceParser {
    for (const parser of this.parsers) {
      if (parser.supports(type)) {
        return parser
      }
    }

    throw new Error(`Unsupported algorithm type ${type}`)
  }

  parse(price: BaseBillablePrice): BillablePrice {
    return this.getParserForPrice(price.algorithm.type).parse(price)
  }

  parseAmountWithTypeFromConstantsAndCoefficients(type: string, constants: Amount[], coefficients: Amount[]): AmountWithType[] {
    return this.getParserForPrice(type).parseAmountWithTypeFromConstantsAndCoefficients(constants, coefficients)
  }
}

export const mapAssignedPriceToBillablePrice = (assignedPrice: BaseBillablePrice): BillablePrice => {
  return new PriceParser().parse(assignedPrice)
}

export const mapBillableItemToBillable = (billableItem: SwaggerBillableItem): Billable => ({
  billableId: billableItem.id,
  name: billableItem.name,
  unitName: billableItem.unitName,
})

const mapAllowedQuantityToQuantity = (allowedQuantity: SwaggerBillableItem['allowedQuantity']): CustomerDiscount['quantity'] => {
  switch (allowedQuantity.type) {
    case 'Exactly': {
      return {
        type: 'exact',
        value: allowedQuantity.n,
      }
    }

    case 'Range': {
      return {
        type: 'range',
        min: allowedQuantity.fromExclusive,
        max: allowedQuantity.toInclusive,
      }
    }
  }
}

export interface DiscountMapper {
  supports(discount: Discount): boolean
  supportsType(discountType: string): boolean
  asDiscounts(discount: Discount): Discounts
  supportsCreateDiscount(request: CreateDiscount): boolean
  asCreateDiscountRequest(request: CreateDiscount): CreateDiscountRequest
  discountTypeLabel: string
}

export const getMapperFromDiscountType = (discountType: string): DiscountMapper | undefined => {
  const mappers: DiscountMapper[] = [
    new FlatDiscountDtoMapper(),
    new PerUnitDiscountDtoMapper(),
    new PerUnitAndFlatDiscountDtoMapper(),
    new FirstNFreeDiscountDtoMapper(),
  ]

  for (const mapper of mappers) {
    if (mapper.supportsType(discountType)) {
      return mapper
    }
  }
}

export const getDiscountMapperForDiscount = (discount: Discount): DiscountMapper | undefined => {
  const mappers: DiscountMapper[] = [
    new FlatDiscountDtoMapper(),
    new PerUnitDiscountDtoMapper(),
    new PerUnitAndFlatDiscountDtoMapper(),
    new FirstNFreeDiscountDtoMapper(),
  ]

  for (const mapper of mappers) {
    if (mapper.supports(discount)) {
      return mapper
    }
  }
}

const mapDiscountDtoToAmount = (discount: Discount): Discounts => {
  const mapper = getDiscountMapperForDiscount(discount)

  if (!mapper) {
    throw new Error(`Unsupported discount type: ${discount?.discountType?.type}`)
  }

  return mapper.asDiscounts(discount)
}

export const mapCreateDiscountToCreateDiscountRequest = (request: CreateDiscount): CreateDiscountRequest => {
  const mappers = [
    new FlatDiscountDtoMapper(),
    new PerUnitDiscountDtoMapper(),
    new PerUnitAndFlatDiscountDtoMapper(),
    new FirstNFreeDiscountDtoMapper(),
  ]

  for (const mapper of mappers) {
    if (mapper.supportsCreateDiscount(request)) {
      return mapper.asCreateDiscountRequest(request)
    }
  }

  throw new Error(`Unsupported discount type: ${request.discountType.type}`)
}

export const mapTemplate = (template: ContractTemplate): Template => ({
  id: template.id,
  name: template.name,
  externalName: template.externalName,
})

export const mapDiscountDtoToCustomerDiscount = (billable: SwaggerBillableItem, discount: Discount): CustomerDiscount => ({
  billableId: billable.id,
  name: billable.name,
  unit: billable.unitName,
  quantity: mapAllowedQuantityToQuantity(billable.allowedQuantity),
  discount: mapDiscountDtoToAmount(discount),
})
